import React, { useEffect, useState } from 'react';
import { useNavigation } from '../contexts/NavigationContext';

export function NavigationProgress() {
  const { isNavigating, isInitialLoad } = useNavigation();
  const [progress, setProgress] = useState(0);
  const [isMounted, setIsMounted] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);

  // Only render on client after hydration to prevent SSR mismatch
  useEffect(() => {
    setIsMounted(true);
    
    // Check if we've navigated before (not initial load)
    // Wait a bit to ensure the flag is set after initial page transition
    const timeout = setTimeout(() => {
      if (typeof window !== 'undefined') {
        const hasNavigated = (window as any).__vikeHasNavigated === true;
        // Only show if we've navigated before (not initial SSR load)
        setShouldShow(hasNavigated && !isInitialLoad);
      }
    }, 300);
    
    return () => clearTimeout(timeout);
  }, [isInitialLoad]);

  useEffect(() => {
    // Don't run progress animation during SSR, before hydration, or on initial load
    if (!isMounted || !shouldShow || isInitialLoad) return;

    if (isNavigating) {
      // Start progress animation immediately
      setProgress(0);
      // Simulate progress - start fast, then slow down
      const interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) {
            clearInterval(interval);
            return 90; // Don't go to 100% until navigation completes
          }
          // Exponential easing - fast start, slow end
          const increment = (100 - prev) * 0.1;
          return Math.min(prev + increment, 90);
        });
      }, 50);
      return () => clearInterval(interval);
    } else {
      // Complete the progress bar quickly when navigation finishes
      setProgress(100);
      const timeout = setTimeout(() => {
        setProgress(0);
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, [isNavigating, isMounted, isInitialLoad, shouldShow]);

  // Don't render during SSR, before hydration, or on initial load
  // Also don't render if not navigating and progress is 0
  if (!isMounted || !shouldShow || isInitialLoad || (!isNavigating && progress === 0)) {
    return null;
  }

  return (
    <>
      {/* Progress bar at the top */}
      <div
        className="fixed top-0 left-0 right-0 z-[100] h-1 bg-transparent pointer-events-none"
        style={{ height: '3px' }}
      >
        <div
          className="h-full bg-gradient-to-r from-green-500 via-blue-500 to-green-500 transition-all duration-300 ease-out"
          style={{
            width: `${progress}%`,
            boxShadow: '0 0 15px rgba(34, 197, 94, 0.6), 0 0 30px rgba(34, 197, 94, 0.3)',
            transition: progress === 100 ? 'width 0.2s ease-out' : 'width 0.05s linear',
          }}
        />
      </div>
      {/* Subtle loading overlay for better visual feedback */}
      {isNavigating && progress > 5 && (
        <div
          className="fixed inset-0 z-[99] pointer-events-none bg-gradient-to-b from-white/10 via-transparent to-transparent dark:from-black/10 transition-opacity duration-300"
          style={{
            opacity: Math.min(progress / 30, 0.4),
          }}
        />
      )}
    </>
  );
}

